import { QuoteError } from '~/lib/api/smartsend/types';

/**
 * Sub-classifies `errorcodes/service/service-ineligible`, the one Type quoting uses for every
 * out-of-bounds measure - around 33 situations, from "too heavy" to "too many parcels".
 *
 * This reads prose that is not part of the response contract, so it is quarantined in this one file
 * rather than spread through the copy. It is safe enough because the prose is generated from a fixed set
 * of values, not written: `Title` reads "{Measure} Out Of Bounds" and `Detail` "Item '{id}' had {Measure}
 * {Reason}", with 11 possible measures and 3 reasons. A parse matching neither shows the reported
 * sentence unchanged, so being wrong costs nothing.
 *
 * The whole file can go if those two values ever arrive as fields of their own.
 */

/**
 * Measure tokens exactly as they are reported, mapped to what a customer cares about. Longest first:
 * 'WEIGHT' is a substring of 'VOLUMETRIC WEIGHT', 'LENGTH' of 'LENGTH+GIRTH'.
 *
 * Every dimensional measure collapses to SIZE on purpose. Girth and volumetric weight are courier pricing
 * concepts with no counterpart in the length/width/height a customer types into the parcel form, so naming
 * the breached limit would explain less than "too big" does. The action is the same either way.
 */
const Measures: [string, string][] = [
  ['VOLUMETRIC WEIGHT', 'WEIGHT'],
  ['WEIGHT', 'WEIGHT'],
  ['LENGTH+GIRTH', 'SIZE'],
  ['LENGTH+WIDTH', 'SIZE'],
  ['COMBINED DIMENSIONS', 'SIZE'],
  ['LENGTH', 'SIZE'],
  ['WIDTH', 'SIZE'],
  ['HEIGHT', 'SIZE'],
  ['VOLUME', 'SIZE'],
  ['QUANTITY', 'QUANTITY'],
  ['VALUE', 'VALUE']
];

const Reasons: [string, string][] = [
  ['OVER MAXIMUM BOUNDS', 'OVER'],
  ['UNDER MINIMUM BOUNDS', 'UNDER'],
  ['IS REQUIRED', 'REQUIRED']
];

/**
 * What is out of bounds and which way, as `WEIGHT-OVER`, or empty when this is not an ineligibility or
 * the prose has changed shape. Used both to narrow the translation key and to choose the copy below.
 */
export const ServiceIneligibilitySubCode = (code: string, error: QuoteError | undefined): string => {
  // Guarded here rather than left to callers so this can never misfire on another Type.
  if (code !== 'SERVICE-INELIGIBLE') {
    return '';
  }

  const text = `${error?.Title ?? ''} ${error?.Detail ?? ''}`.toUpperCase();
  const measure = Measures.find(([token]) => text.includes(token));
  const reason = Reasons.find(([token]) => text.includes(token));

  return measure && reason ? `${measure[1]}-${reason[1]}` : '';
};

/** Plain English for a sub-code, or empty to leave the reported sentence in place. */
export const ServiceIneligibilityMessage = (subCode: string): string => {
  switch (subCode) {
    case 'WEIGHT-OVER':
      return 'This parcel is too heavy for this service.';
    case 'WEIGHT-UNDER':
      return 'This parcel is too light for this service.';
    case 'WEIGHT-REQUIRED':
      return 'Please enter the weight of this parcel.';

    case 'SIZE-OVER':
      return 'This parcel is too big for this service.';
    case 'SIZE-UNDER':
      return 'This parcel is too small for this service.';
    case 'SIZE-REQUIRED':
      return 'Please enter the dimensions of this parcel.';

    case 'QUANTITY-OVER':
      return 'This shipment has too many parcels for this service.';
    case 'QUANTITY-UNDER':
      return 'This shipment has too few parcels for this service.';

    case 'VALUE-OVER':
      return "This parcel's value is too high for this service.";
    case 'VALUE-UNDER':
      return "This parcel's value is too low for this service.";
    case 'VALUE-REQUIRED':
      return 'Please enter the value of this parcel.';
  }

  return '';
};

/**
 * Where to send the customer for a sub-code. Both actions are honest for an out-of-bounds parcel - change
 * the measure, or pick a courier that accepts it - so the service link stays alongside the specific one.
 */
export const ServiceIneligibilityResolutions = (subCode: string): string[] => {
  const measure = subCode.split('-')[0];

  switch (measure) {
    case 'WEIGHT':
      return ['ITEM-WEIGHT-REVIEW', 'SERVICE-CHANGE'];
    case 'SIZE':
      return ['ITEM-DIMENSIONS-REVIEW', 'SERVICE-CHANGE'];
    case 'VALUE':
      return ['ITEM-VALUE-REVIEW', 'SERVICE-CHANGE'];
  }

  return [];
};
